import { createClientId } from "../core/client-id.js";

const CHANNEL_NAME = "flexcharting-local-alerts-v1";
const LEASE_KEY = "flexcharting-local-alerts-leader-v1";
const LEASE_DURATION_MS = 6_000;
const HEARTBEAT_MS = 2_000;

export class LocalAlertCoordinator {
  constructor({
    storageScope = "anonymous",
    onMessage = () => {},
    onLeadershipChanged = () => {}
  } = {}) {
    this.tabId = createClientId();
    this.leaseKey = `${LEASE_KEY}:${storageScope}`;
    this.onMessage = onMessage;
    this.onLeadershipChanged = onLeadershipChanged;
    this.isLeader = null;
    this.channel = typeof BroadcastChannel === "undefined"
      ? null
      : new BroadcastChannel(`${CHANNEL_NAME}:${storageScope}`);
    this.channel?.addEventListener("message", (event) => this.onMessage(event.data || {}));
    this.handleStorage = (event) => {
      if (event.key === this.leaseKey) this.refreshLeadership();
    };
    window.addEventListener("storage", this.handleStorage);
  }

  start() {
    this.refreshLeadership();
    this.timer = window.setInterval(() => this.refreshLeadership(), HEARTBEAT_MS);
  }

  publish(message) {
    this.channel?.postMessage(message);
  }

  submitObservation(observation) {
    if (this.isLeader || !this.channel) {
      this.onMessage({ type: "observation", observation });
      return;
    }
    this.publish({ type: "observation", observation });
  }

  dispose() {
    if (this.timer) window.clearInterval(this.timer);
    window.removeEventListener("storage", this.handleStorage);
    this.channel?.close();
    if (this.isLeader) {
      try {
        const lease = readLease(this.leaseKey);
        if (lease?.owner === this.tabId) localStorage.removeItem(this.leaseKey);
      } catch {
        // Storage can be disabled; the in-tab fallback has no lease to release.
      }
    }
  }

  refreshLeadership() {
    let next = true;
    try {
      const now = Date.now();
      const lease = readLease(this.leaseKey);
      if (!lease || lease.expiresAt <= now || lease.owner === this.tabId) {
        localStorage.setItem(this.leaseKey, JSON.stringify({
          owner: this.tabId,
          expiresAt: now + LEASE_DURATION_MS
        }));
      }
      next = readLease(this.leaseKey)?.owner === this.tabId;
    } catch {
      next = true;
    }
    if (next === this.isLeader) return;
    this.isLeader = next;
    this.onLeadershipChanged(next);
  }
}

function readLease(key) {
  const value = localStorage.getItem(key);
  if (!value) return null;
  try {
    const lease = JSON.parse(value);
    return lease?.owner && Number.isFinite(Number(lease.expiresAt))
      ? { owner: lease.owner, expiresAt: Number(lease.expiresAt) }
      : null;
  } catch {
    return null;
  }
}
